import React from 'react';
import { useField, connect } from 'formik';
import { components } from 'react-select';
import styled from 'styled-components';

import FieldContextFeeder from '../components/FieldContextFeeder';

import { Select, Checkbox } from '~ui-kit';

// SelectIdField represent the inner value as an id instead of the
// object required by react-select

// Why?

// Because this way we can have symmetric representation of the data in both
// the frontend and the backend which should make actions like editing
// an order easier

const idsToOptions = (ids, options) => {
  return ids?.map(id => options?.find(option => option.value === id));
};

const optionsToIds = options => {
  return options?.map(option => option?.value);
};

const Control = ({ children, ...props }) => {
  const { value } = props.selectProps;

  return <components.Control {...props}>{children}</components.Control>;
};

const Option = ({ children, ...props }) => {
  const { value: option } = props;
  const { value } = props.selectProps;

  const active = value?.find(v => v.value === option);

  return (
    <components.Option {...props} type="visible">
      <OptionWrapper>
        <Checkbox value={active} /> {children}
      </OptionWrapper>
    </components.Option>
  );
};

const ValueContainer = ({ children, ...props }) => {
  const [values, input] = children;
  let labels = '';

  // A value for this could be found dynamically but it's a lot of work
  const maxCharacterCount = props.selectProps.maxCharacterCount || 20;

  if (Array.isArray(values)) {
    // See if we need ", +n"
    const needEllipsis = values.join(', ').length > maxCharacterCount;

    // eslint-disable-next-line no-restricted-syntax, no-plusplus
    for (let i = 0; i < values.length; i++) {
      const { label } = values[i].props.data;
      if (
        labels.length + 2 + label.length + (needEllipsis ? 3 : 0) <=
        maxCharacterCount
      ) {
        if (labels.length > 0) {
          labels += ', ';
        }
        labels += label;
      } else {
        labels += `, +${values.length - i}`;
        break;
      }
    }
  }

  return (
    <components.ValueContainer {...props}>
      <ValueContainerWrapper>
        {labels || <Placeholder>Valitse...</Placeholder>}
        {input}
      </ValueContainerWrapper>
    </components.ValueContainer>
  );
};

const styles = {
  option: (styles, { isFocused }) => ({
    ...styles,
    alignItems: 'center',
    display: 'flex',

    color: 'inherit',
    backgroundColor: isFocused ? '#DEEBFF' : 'white',
  }),
};

const MultiSelectIdField = props => {
  const [field, meta] = useField(props);
  const error = meta.touched && meta.error;
  const { setFieldValue } = props.formik;

  const name = props.id || props.name;

  const localProps = {
    ...field,
    ...props,
    error,
    value: idsToOptions(field.value, props.options),
    // https://github.com/formium/formik/issues/789
    onChange: options => {
      const id = optionsToIds(options);
      setFieldValue(name, id);
      if (props.onChange) props.onChange(id);
    },
  };

  return (
    <Container className={props.className}>
      <Select
        {...localProps}
        isMulti
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        isSearchable={false}
        isClearable={false}
        autoFocus={false}
        components={{ Control, Option, ValueContainer }}
        styles={styles}
      />
    </Container>
  );
};

const Container = styled.div`
  max-width: var(--field-width);
`;

const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ValueContainerWrapper = styled.div`
  position: relative;
`;

const Placeholder = styled.span`
  color: hsl(0, 0%, 50%);
`;

export default FieldContextFeeder(connect(MultiSelectIdField));
